import { getPrimaryOrigin, IconWrapper } from '@flexo/general';
import {
  ALERT,
  alertCallbackStore,
  METADATA,
  RIGHT_DRAWER,
} from '@flexo/reducers';
import {
  CircularProgress,
  TableCell,
  TableFooter,
  TableSortLabel,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TableSkeleton } from '../skeletons/table-skeleton';
import { adminUserMatchesSearch } from '../admin-users-search.utils';
import uStyles from './admin-users-table.module.scss';
import { Collapsible } from './helpers';
import { useStickyTableFooterEntry } from './sticky-table-footer';
import styles from './table.module.scss';

const ENV = (import.meta as any).env;
const EMPTY_COMMUNITIES: any[] = [];

const getMetadataCommunities = (metadata: any) =>
  [
    metadata?.communities,
    metadata?.entities?.community,
    metadata?.entities?.communities,
    metadata?.sortedEntities?.communities,
  ].find((communities) => Array.isArray(communities) && communities.length > 0) ||
  EMPTY_COMMUNITIES;

/**
 * AdminUsersInvitedTable
 * Infinite-scroll table for invited admin users.
 * Loads batches of 50 automatically as the user scrolls down.
 */
export function AdminUsersInvitedTable() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);

  const [loadingResend, setLoadingResend] = useState<Record<string, boolean>>(
    {}
  );
  const [loadingDisable, setLoadingDisable] = useState<Record<string, boolean>>(
    {}
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const expandedBeforeSearch = useRef<boolean | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const titlePlaceholderRef = useRef<HTMLDivElement>(null);
  const isExpandedRef = useRef(isExpanded);
  isExpandedRef.current = isExpanded;
  // JS-based sticky title + header via rAF polling
  useEffect(() => {
    let titleStuck = false;
    let headerStuck = false;
    let headerHeight = 0;
    let titleHeight = 0;
    let rafId: number | null = null;
    let scrollContainer: HTMLElement | null = null;
    let fixedHeaderBottom = 0;

    const findScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null;

      const ionContent = el.closest('ion-content') as any;
      if (ionContent?.shadowRoot) {
        const innerScroll = ionContent.shadowRoot.querySelector('.inner-scroll');
        if (innerScroll) return innerScroll as HTMLElement;
      }

      let parent: HTMLElement | null = el.parentElement;
      while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }

      return null;
    };

    const measureFixedHeaderBottom = (anchor: HTMLElement): number => {
      let maxBottom = 0;

      const check = (el: Element) => {
        const computed = window.getComputedStyle(el as HTMLElement);
        if (computed.position !== 'fixed') return;

        const rect = (el as HTMLElement).getBoundingClientRect();
        if (rect.top <= 2 && rect.height > 0 && rect.bottom > maxBottom) {
          maxBottom = rect.bottom;
        }
      };

      let node: HTMLElement | null = anchor;
      while (node) {
        let sibling = node.previousElementSibling;
        while (sibling) {
          check(sibling);
          sibling.querySelectorAll('*').forEach(check);
          sibling = sibling.previousElementSibling;
        }
        node = node.parentElement;
      }

      return maxBottom;
    };

    const refreshFixedHeader = () => {
      if (!titleRef.current) return;
      scrollContainer =
        scrollContainer || findScrollContainer(titleRef.current);
      fixedHeaderBottom = measureFixedHeaderBottom(titleRef.current);
    };

    const unstickTitle = () => {
      const title = titleRef.current;
      const titlePlaceholder = titlePlaceholderRef.current;
      if (title) {
        title.style.position = '';
        title.style.top = '';
        title.style.left = '';
        title.style.width = '';
        title.style.zIndex = '';
        title.style.backgroundColor = '';
        title.style.backdropFilter = '';
        title.style.paddingTop = '';
        title.style.paddingBottom = '';
      }
      if (titlePlaceholder) {
        titlePlaceholder.style.height = '0px';
      }
      titleStuck = false;
    };

    const unstickHeader = () => {
      const header = headerRef.current;
      const placeholder = placeholderRef.current;
      if (header) {
        header.style.position = '';
        header.style.top = '';
        header.style.width = '';
        header.style.left = '';
        header.style.zIndex = '';
      }
      if (placeholder) {
        placeholder.style.height = '0px';
      }
      headerStuck = false;
    };

    const update = () => {
      if (!isExpandedRef.current) {
        if (titleStuck) unstickTitle();
        if (headerStuck) unstickHeader();
        return;
      }

      if (!scrollContainer && titleRef.current) {
        scrollContainer = findScrollContainer(titleRef.current);
      }
      if (!fixedHeaderBottom && titleRef.current) {
        fixedHeaderBottom = measureFixedHeaderBottom(titleRef.current);
      }

      const baseline = Math.max(
        scrollContainer?.getBoundingClientRect().top || 0,
        fixedHeaderBottom
      );

      // Sticky collapse title
      const titlePh = titlePlaceholderRef.current;
      const title = titleRef.current;
      const tableRect = tableRef.current?.getBoundingClientRect();
      const titlePhRect = titlePh?.getBoundingClientRect();
      if (
        !tableRect ||
        tableRect.width === 0 ||
        tableRect.height === 0 ||
        !titlePhRect ||
        titlePhRect.width === 0
      ) {
        if (titleStuck) unstickTitle();
        if (headerStuck) unstickHeader();
        return;
      }
      if (!titlePh || !title) {
        titleStuck = false;
        titleHeight = 0;
      }
      if (titlePh && title) {
        const titleRect = titlePh.getBoundingClientRect();
        if (titleRect.top <= baseline && !titleStuck) {
          titleHeight = title.offsetHeight;
          titleStuck = true;
          titlePh.style.height = `${titleHeight}px`;
          title.style.position = 'fixed';
          title.style.top = `${baseline}px`;
          title.style.left = `${titleRect.left}px`;
          title.style.width = `${titleRect.width}px`;
          title.style.zIndex = '11';
        } else if (titleRect.top > baseline && titleStuck) {
          titleStuck = false;
          titlePh.style.height = '0px';
          title.style.position = '';
          title.style.top = '';
          title.style.left = '';
          title.style.width = '';
          title.style.zIndex = '';
          title.style.backgroundColor = '';
          title.style.backdropFilter = '';
          title.style.paddingTop = '';
          title.style.paddingBottom = '';
        } else if (titleStuck) {
          title.style.left = `${titleRect.left}px`;
          title.style.width = `${titleRect.width}px`;
          title.style.top = `${baseline}px`;
        }
      }

      const placeholder = placeholderRef.current;
      const header = headerRef.current;
      if (!placeholder || !header) {
        headerStuck = false;
        headerHeight = 0;
        return;
      }

      const rect = placeholder.getBoundingClientRect();
      const headerThreshold = baseline + (titleStuck ? titleHeight : 0);

      if (rect.top <= headerThreshold && !headerStuck) {
        headerHeight = header.offsetHeight;
        headerStuck = true;
        placeholder.style.height = `${headerHeight}px`;
        header.style.position = 'fixed';
        header.style.top = `${headerThreshold}px`;
        header.style.width = `${rect.width}px`;
        header.style.left = `${rect.left}px`;
        header.style.zIndex = '10';
      } else if (rect.top > headerThreshold && headerStuck) {
        headerStuck = false;
        placeholder.style.height = '0px';
        header.style.position = '';
        header.style.top = '';
        header.style.width = '';
        header.style.left = '';
        header.style.zIndex = '';
      } else if (headerStuck) {
        header.style.left = `${rect.left}px`;
        header.style.width = `${rect.width}px`;
        header.style.top = `${headerThreshold}px`;
      }
    };

    refreshFixedHeader();
    window.addEventListener('resize', refreshFixedHeader);
    const refreshTimer = window.setInterval(refreshFixedHeader, 1000);

    const poll = () => {
      update();
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', refreshFixedHeader);
      window.clearInterval(refreshTimer);
      unstickTitle();
      unstickHeader();
    };
  }, []);

  useEffect(() => {
    if (isExpanded) return;

    if (titlePlaceholderRef.current) {
      titlePlaceholderRef.current.style.height = '0px';
    }
    if (titleRef.current) {
      titleRef.current.style.position = '';
      titleRef.current.style.top = '';
      titleRef.current.style.left = '';
      titleRef.current.style.width = '';
      titleRef.current.style.zIndex = '';
      titleRef.current.style.backgroundColor = '';
      titleRef.current.style.backdropFilter = '';
      titleRef.current.style.paddingTop = '';
      titleRef.current.style.paddingBottom = '';
    }
    if (placeholderRef.current) {
      placeholderRef.current.style.height = '0px';
    }
    if (headerRef.current) {
      headerRef.current.style.position = '';
      headerRef.current.style.top = '';
      headerRef.current.style.width = '';
      headerRef.current.style.left = '';
      headerRef.current.style.zIndex = '';
    }
  }, [isExpanded]);

  const skeletonTitle = t(
    'widgets.tables.AdminUsersInvitedTable.skeletonTitle'
  );

  const getUserId = (row: any) =>
    row._raw?.userId || row.userId || row._raw?.clerkUserId || row.clerkUserId;

  const handleResendInvitation = async (row: any) => {
    const userId = getUserId(row);
    const organizationId = user?.organizationId || ENV.VITE_APP_ORG_ID;

    if (!userId) {
      console.error(
        'Cannot resend invitation: neither userId nor clerkUserId is available'
      );
      return;
    }

    setLoadingResend((prev) => ({ ...prev, [userId]: true }));

    const locale = i18n.language?.includes('_')
      ? i18n.language.split('_')[1]
      : i18n.language || 'en';

    try {
      await dispatch(
        METADATA.ResendInvitationAsync({
          dispatch,
          organizationId,
          userId,
          locale,
          language: locale,
          inviteUrl: `${getPrimaryOrigin()}/register-by-invite?email=${encodeURIComponent(
            row.email
          )}`,
        }) as any
      );
    } finally {
      setLoadingResend((prev) => ({ ...prev, [userId]: false }));
    }
  };

  function handleRowClick(row: any) {
    if (row._optimistic || isStillFetching) return;
    dispatch(
      RIGHT_DRAWER.SetDrawer({ type: 'ADMIN_UPDATE_USER', data: row } as any)
    );
  }

  const handleDisableUser = (row: any, e: any) => {
    e.stopPropagation();
    e.preventDefault();

    const userId = getUserId(row);
    const organizationId = user?.organizationId || ENV.VITE_APP_ORG_ID;

    if (!userId) {
      console.error('Cannot disable user: no userId available');
      return;
    }

    alertCallbackStore.setData({
      userId,
      organizationId,
    });

    dispatch(
      ALERT.SetAlert({
        value: {
          type: 'ALERT_REMOVE_USER',
        },
      } as any)
    );
  };

  // Titles and headers
  const { title, rows } = t('widgets.tables.AdminUsersInvitedTable', {
    returnObjects: true,
  }) as any;
  const getTitleByField = (field: string, fallback: string) =>
    rows?.find((r: any) => r.field === field)?.title || fallback;
  const getSortableByField = (field: string, fallback = true) =>
    (rows?.find((r: any) => r.field === field)?.sortable ??
      fallback) as boolean;
  const thEmail = getTitleByField('email', 'Email');
  const thInvited = getTitleByField('invited', 'Invited');
  const thExpiry = getTitleByField('expiry', 'Expiry');
  const thRole = getTitleByField('role', 'Role');
  const thCommunities = getTitleByField('communities', 'Communities');
  const thActions = getTitleByField('actions', 'Actions');
  const columns = [
    { title: thEmail, field: 'email', sortable: getSortableByField('email') },
    {
      title: thInvited,
      field: 'invited',
      sortable: getSortableByField('invited'),
    },
    {
      title: thExpiry,
      field: 'expiry',
      sortable: getSortableByField('expiry'),
    },
    { title: thRole, field: 'role', sortable: getSortableByField('role') },
    {
      title: thCommunities,
      field: 'communities',
      sortable: getSortableByField('communities'),
    },
    { title: thActions, field: 'actions', sortable: false },
  ] as const;

  const allInvitedUsers =
    useSelector((state: any) => state.metadata?.adminsInvited) || [];
  const pagination = useSelector(
    (state: any) => state.metadata?.adminsInvitedPagination
  );
  const loadingStatus = useSelector(
    (state: any) => state.metadata?.AdminsInvitedAsyncStatus
  );
  const adminSearchText =
    useSelector((state: any) => state.metadata?.adminSearchText) || '';
  const allCommunities = useSelector((state: any) =>
    getMetadataCommunities(state.metadata)
  );
  const organizationId = user?.organizationId || ENV.VITE_APP_ORG_ID;

  // Filter out expired invites and invalidate them on the backend
  const { validInvites, expiredInvites } = useMemo(() => {
    const now = new Date();
    const valid: any[] = [];
    const expired: any[] = [];
    for (const invite of allInvitedUsers) {
      const expiresAt = invite._raw?.expiresAt;
      if (expiresAt && new Date(expiresAt) < now) {
        expired.push(invite);
      } else {
        valid.push(invite);
      }
    }
    return { validInvites: valid, expiredInvites: expired };
  }, [allInvitedUsers]);

  // Invalidate expired invites by disabling them
  useEffect(() => {
    if (expiredInvites.length === 0) return;
    for (const row of expiredInvites) {
      const userId = getUserId(row);
      if (!userId) continue;
      dispatch(
        METADATA.DisableUserAsync({
          dispatch,
          organizationId,
          userId,
        }) as any
      );
    }
  }, [expiredInvites, dispatch, organizationId]);

  // Client-side sort state
  const [localSortBy, setLocalSortBy] = useState<string>('invited');
  const [localSortDir, setLocalSortDir] = useState<'asc' | 'desc'>('desc');

  const invitedUsers = useMemo(() => {
    let filtered = validInvites;
    if (adminSearchText) {
      filtered = validInvites.filter((a: any) =>
        adminUserMatchesSearch(a, adminSearchText, allCommunities)
      );
    }
    const sorted = [...filtered];
    sorted.sort((a: any, b: any) => {
      const valA = (a?.[localSortBy] || '').toString().toLowerCase();
      const valB = (b?.[localSortBy] || '').toString().toLowerCase();
      if (valA < valB) return localSortDir === 'asc' ? -1 : 1;
      if (valA > valB) return localSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [validInvites, adminSearchText, allCommunities, localSortBy, localSortDir]);

  const invitedUsersStickyFooter = useMemo(
    () => () => (
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        }}
      >
        <Table aria-label="admin invited users sticky footer">
          <TableFooter>
            <TableRow>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__email}`}
              >
                <div className={styles.Table__Footer__Cell}>
                  <span className={uStyles.AdminUsers__FooterCount}>
                    {t('adminUsers.hints.countInvited', {
                      count: invitedUsers.length,
                      defaultValue: `${invitedUsers.length} Invited`,
                    })}
                  </span>
                </div>
              </TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__invited}`}
              ></TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__expiry}`}
              ></TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__role}`}
              ></TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__communities}`}
              ></TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__actions}`}
              ></TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    ),
    [invitedUsers.length, t]
  );

  const hasStickyFooterHost = useStickyTableFooterEntry(
    useMemo(
      () =>
        invitedUsers.length > 0
          ? {
              id: 'admin-users-invited',
              enabled: isExpanded,
              sectionRef: tableRef,
              focusRef: titlePlaceholderRef,
              viewportRef: tableRef,
              contentRef: tableRef,
              renderFooter: invitedUsersStickyFooter,
            }
          : null,
      [invitedUsers.length, isExpanded, invitedUsersStickyFooter]
    )
  );

  // Auto-expand when search finds results, restore on clear
  useEffect(() => {
    if (adminSearchText) {
      if (expandedBeforeSearch.current === null) {
        expandedBeforeSearch.current = isExpanded;
      }
      if (invitedUsers.length > 0) {
        setIsExpanded(true);
      }
    } else if (expandedBeforeSearch.current !== null) {
      setIsExpanded(expandedBeforeSearch.current);
      expandedBeforeSearch.current = null;
    }
  }, [adminSearchText, invitedUsers.length]);

  const currentPage = pagination?.page || 1;
  const currentPageSize = pagination?.pageSize || 50;
  const totalItems = pagination?.totalItems || 0;
  const totalPages = pagination?.totalPages || 1;
  const currentSortBy = pagination?.sortBy || 'invited';
  const currentSortDir = pagination?.sortDir || 'desc';
  const currentSearch = pagination?.search || '';
  const hasMore = currentPage < totalPages;
  const isLoadingMore = loadingStatus === 'LOADING_MORE';
  const isStillFetching =
    hasMore || isLoadingMore || loadingStatus === 'PENDING';

  // Auto-fetch next page as soon as current one completes
  useEffect(() => {
    if (loadingStatus === 'SUCCESS' && hasMore) {
      dispatch(
        METADATA.SetAdminsPaginatedAsync({
          dispatch,
          organizationId,
          status: 'invited',
          page: currentPage + 1,
          pageSize: currentPageSize,
          search: currentSearch || undefined,
          sortBy: currentSortBy,
          sortDir: currentSortDir,
          isLoadMore: true,
        }) as any
      );
    }
  }, [
    loadingStatus,
    hasMore,
    currentPage,
    currentPageSize,
    currentSearch,
    currentSortBy,
    currentSortDir,
    organizationId,
  ]);

  const dispatchFresh = useCallback(
    (overrides: Record<string, any> = {}) => {
      dispatch(
        METADATA.SetAdminsPaginatedAsync({
          dispatch,
          organizationId,
          status: 'invited',
          page: 1,
          pageSize: currentPageSize,
          search: currentSearch || undefined,
          sortBy: currentSortBy,
          sortDir: currentSortDir,
          isRefresh: true,
          isLoadMore: false,
          ...overrides,
        }) as any
      );
    },
    [
      dispatch,
      organizationId,
      currentPageSize,
      currentSearch,
      currentSortBy,
      currentSortDir,
    ]
  );

  function handleSort(field: string) {
    if (isStillFetching) return;
    if (localSortBy === field) {
      setLocalSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setLocalSortBy(field);
      setLocalSortDir('asc');
    }
  }

  // Skeleton columns for loading state
  const skeletonColumns = [
    { title: thEmail },
    { title: thInvited },
    { title: thExpiry },
    { title: thRole },
    { title: thCommunities },
    { title: thActions },
  ];

  // Show skeleton while loading initial data
  if (loadingStatus === 'PENDING') {
    return (
      <div className={`${styles.Table} ${uStyles.AdminUsersTable} pt-2_5`}>
        <TableSkeleton
          rows={skeletonColumns}
          context="adminUsers"
          hasShimmer={true}
          title={skeletonTitle}
        />
      </div>
    );
  }

  if (invitedUsers.length === 0 && !isLoadingMore && !adminSearchText) {
    return <></>;
  }

  return (
    <div
      ref={tableRef}
      className={`${styles.Table} ${uStyles.AdminUsersTable} ${
        !isExpanded ? uStyles.AdminUsersTableCollapsed : ''
      }`}
      style={{ display: invitedUsers.length === 0 ? 'none' : undefined }}
    >
      <div ref={titlePlaceholderRef} />
      <div
        ref={titleRef}
        className={`${styles.Table__Title} ${uStyles.AdminUsers__Title} ${
          uStyles.AdminUsers__CollapseHeader
        } ${
          isExpanded
            ? uStyles.AdminUsers__CollapseHeader__Expanded
            : uStyles.AdminUsers__CollapseHeader__Collapsed
        }`}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span>{title}</span>
        <IconWrapper
          iconName="chevron-down"
          className={uStyles.AdminUsers__CollapseChevron}
        />
      </div>
      {isExpanded && (
        <div
          ref={placeholderRef}
          className={uStyles.AdminUsers__StickyHeaderPlaceholder}
        />
      )}
      {isExpanded && (
        <div
          ref={headerRef}
          className={uStyles.AdminUsers__StickyHeaderWrapper}
        >
          <TableContainer
            component={Paper}
            className={uStyles.AdminUsers__StickyHeader}
          >
            <Table aria-label="admin invited users header">
              <TableHead>
                <TableRow>
                  {columns.map((column: any, idx: number) => (
                    <TableCell
                      key={`invited_head_${column.field}_${idx}`}
                      className={`
                      ${
                        column.sortable
                          ? styles.Table__Header__Cell__Sortable
                          : styles.Table__Header__Cell__Static
                      }
                      ${
                        column.sortable && localSortBy === column.field
                          ? styles.Table__Header__Cell__Active
                          : styles.Table__Header__Cell
                      }
                      ${uStyles.AdminUsers__TH}
                      ${
                        (uStyles as any)[
                          'AdminUsers__Col__' + String(column.field)
                        ] || ''
                      }
                    `}
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={localSortBy === column.field}
                          direction={
                            localSortBy === column.field &&
                            localSortDir === 'asc'
                              ? 'asc'
                              : 'desc'
                          }
                          onClick={() => handleSort(column.field)}
                          style={isStillFetching ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
                          className={`${styles.Table__Header__Cell}`}
                          IconComponent={() =>
                            localSortBy === column.field ? (
                              localSortDir === 'asc' ? (
                                <IconWrapper
                                  iconName="chevron-up"
                                  className={`${styles.Table__TH__Icon}`}
                                />
                              ) : (
                                <IconWrapper
                                  iconName="chevron-down"
                                  className={`${styles.Table__TH__Icon}`}
                                />
                              )
                            ) : (
                              <div style={{ height: 32, width: 19 }}></div>
                            )
                          }
                        >
                          <div>{(column.title as string).toUpperCase()}</div>
                        </TableSortLabel>
                      ) : (
                        <div>{(column.title as string).toUpperCase()}</div>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        </div>
      )}
      <Collapsible
        open={isExpanded}
        className={uStyles.AdminUsers__Collapsible}
      >
        <div className={uStyles.AdminUsers__TableWrapper}>
          {/* Rows */}
          {invitedUsers.map((row: any, rowIndex: number) => (
            <TableContainer
              component={Paper}
              key={`invited_row_${getUserId(row) || rowIndex}`}
              className={uStyles.AdminUsers__RowContainer}
            >
              <Table aria-label={`admin invited users row ${rowIndex}`}>
                <TableBody className={uStyles.AdminUsersTableInactive}>
                  <TableRow
                    className={uStyles.AdminUsers__Row}
                    onClick={() => handleRowClick(row)}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      opacity: row._optimistic
                        ? 0.5
                        : isStillFetching
                        ? 0.6
                        : 1,
                      pointerEvents: row._optimistic
                        ? 'none'
                        : isStillFetching
                        ? 'none'
                        : 'auto',
                      cursor:
                        row._optimistic || isStillFetching
                          ? 'default'
                          : 'pointer',
                    }}
                  >
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__email}`}
                    >
                      {row.email}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__invited}`}
                    >
                      <div>{row.invited}</div>
                      {row.reminder && (
                        <div style={{ fontSize: 12, opacity: 0.7 }}>
                          {row.reminder}
                        </div>
                      )}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__expiry}`}
                    >
                      {row.expiry}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__role}`}
                    >
                      {row.role}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__communities}`}
                    >
                      {row.communities}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__actions}`}
                    >
                      <div>
                        <div
                          className="flex flex-align-center"
                          style={{
                            gap: 8,
                            cursor:
                              isStillFetching || loadingResend[getUserId(row)]
                                ? 'not-allowed'
                                : 'pointer',
                            pointerEvents:
                              isStillFetching || loadingResend[getUserId(row)]
                                ? 'none'
                                : 'auto',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              !isStillFetching &&
                              !loadingResend[getUserId(row)]
                            ) {
                              handleResendInvitation(row);
                            }
                          }}
                        >
                          <div
                            className={uStyles.AdminUsers__Icon__Kwick_Wrapper}
                          >
                            {loadingResend[getUserId(row)] ? (
                              <CircularProgress
                                size={16}
                                sx={{ marginLeft: '-4px', marginRight: '12px' }}
                                color="inherit"
                              />
                            ) : (
                              <IconWrapper
                                className={uStyles.AdminUsers__Icon}
                                iconName="kwick_refresh_static"
                              />
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            cursor:
                              isStillFetching || loadingDisable[getUserId(row)]
                                ? 'not-allowed'
                                : 'pointer',
                            opacity:
                              isStillFetching || loadingDisable[getUserId(row)]
                                ? 0.5
                                : 1,
                            pointerEvents:
                              isStillFetching || loadingDisable[getUserId(row)]
                                ? 'none'
                                : 'auto',
                          }}
                          onClick={(e: any) => {
                            if (
                              !isStillFetching &&
                              !loadingDisable[getUserId(row)]
                            ) {
                              handleDisableUser(row, e);
                            }
                          }}
                        >
                          <IconWrapper
                            className={uStyles.AdminUsers__Icon}
                            iconName="delete_kwick"
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ))}
        </div>

        {!hasStickyFooterHost ? (
          <div className={uStyles.AdminUsers__Count}>
            {t('adminUsers.hints.countInvited', {
              count: invitedUsers.length,
              defaultValue: `${invitedUsers.length} Invited`,
            })}
          </div>
        ) : null}
      </Collapsible>
    </div>
  );
}

export default AdminUsersInvitedTable;
